import { z } from "zod";
import { cuidSchema } from "../common/id";

/** Status que ocupam horário de verdade — cancelada e falta liberam a sala e o dentista. */
export const STATUS_QUE_OCUPAM_HORARIO = ["SCHEDULED", "CONFIRMED", "WAITING", "IN_PROGRESS", "COMPLETED", "LATE"] as const;

export type ConsultaNoHorario = {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  dentistId?: string | null;
  salaId?: string | null;
};

/** Intervalos que se cruzam (horas "HH:MM" se comparam como texto): 14h–15h bate com 14h30–15h30,
 *  e 14h–15h não bate com 15h–16h. */
export const sobrepoe = (a: { startTime: string; endTime: string }, b: { startTime: string; endTime: string }) =>
  a.startTime < b.endTime && a.endTime > b.startTime;

/**
 * O que bate com a consulta no mesmo dia: a mesma sala (sempre conflito — uma cadeira não atende
 * duas pessoas) e o mesmo dentista (o servidor só barra se a agenda estiver em "bloquear"; há
 * clínica que encaixa de propósito). Ignora a própria consulta e as canceladas/faltas.
 */
export function conflitosDaConsulta<T extends ConsultaNoHorario>(alvo: Omit<ConsultaNoHorario, "id" | "status"> & { id?: string | null }, existentes: T[]) {
  const ocupando = existentes.filter(
    (e) => e.id !== alvo.id && (STATUS_QUE_OCUPAM_HORARIO as readonly string[]).includes(e.status) && sobrepoe(e, alvo),
  );
  return {
    sala: alvo.salaId ? (ocupando.find((e) => e.salaId === alvo.salaId) ?? null) : null,
    dentista: alvo.dentistId ? (ocupando.find((e) => e.dentistId === alvo.dentistId) ?? null) : null,
  };
}

const corHex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Cor inválida");

export const salaCriarSchema = z.object({
  nome: z.string().trim().min(1, "Dê um nome à sala").max(40),
  cor: corHex.nullable().optional(),
});
export const salaEditarSchema = salaCriarSchema.partial().extend({
  id: z.string().min(1),
  ordem: z.number().int().min(0).max(999).optional(),
});
/** Sala padrão de um profissional por dia da semana ("0" = domingo); sem a chave = sem sala padrão. */
export const salasPadraoSchema = z.object({
  dentistId: cuidSchema,
  salaPorDia: z.partialRecord(z.enum(["0", "1", "2", "3", "4", "5", "6"]), z.string().min(1)),
});
export type SalasPadraoInput = z.infer<typeof salasPadraoSchema>;
